/**
 * Scrolling to a stop, and honouring #entry-id on arrival.
 *
 * Less trivial than element.scrollIntoView() because the cards use
 * content-visibility: everything off screen stands in at its estimated height
 * until the browser renders it for real, so any scroll past unrendered content
 * lands somewhere that stops being correct a frame later.
 *
 * This works from a click, where the page has settled before anyone can press
 * anything. It is NOT sufficient during initial load — see the note at the
 * bottom of this file.
 */

const SETTLE_EVERY_MS = 100;

/*
 * Anything that means "I want to scroll now".
 *
 * wheel and touchmove are the direct gestures. keydown covers arrows, space
 * and page keys.
 *
 * Deliberately NOT the scroll event: the correction fires that on every pass,
 * so it would cancel on the loop's own first move. And deliberately NOT
 * pointerdown: pressing the button that starts the scroll is itself a
 * pointerdown, so including it cancelled the jump on any real click and made
 * the button do nothing at all.
 */
const INTERRUPTS = ["wheel", "touchmove", "keydown"] as const;
/*
 * Hard stop, and deliberately short.
 *
 * It was 4000ms, on the theory that posters keep loading and shifting layout
 * for seconds. True, but the cost was worse than the problem: for four seconds
 * after the button was pressed the page was being re-scrolled six times a
 * second, so a reader who started scrolling was dragged back and the page felt
 * broken. 600ms covers the initial layout settle, which is the part that
 * actually moves the target, and is short enough that nobody experiences it as
 * the scroll fighting them even if the interrupts miss.
 */
const SETTLE_WINDOW_MS = 600;

export type ScrollOutcome = "scrolled" | "not-found";

export function scrollToEntry(id: string): ScrollOutcome {
  const el = document.querySelector<HTMLElement>(`[data-entry-id="${CSS.escape(id)}"]`);
  if (!el) return "not-found";

  // Move focus so keyboard and screen-reader users land where sighted users
  // are looking. -1 keeps it out of the tab order afterwards.
  el.setAttribute("tabindex", "-1");
  el.focus({ preventScroll: true });

  /*
   * Scrolled repeatedly rather than once, because the cards use
   * content-visibility: everything off-screen stands in at its estimated
   * height until rendered, and posters load lazily for seconds after that. A
   * single scrollIntoView lands somewhere that stops being correct a frame
   * later as those estimates are replaced.
   *
   * But the correction MUST yield to the reader. Without that it re-scrolls
   * every 100ms for the full window, so anyone who starts scrolling after
   * pressing the button is dragged back and the page feels broken. Any sign of
   * intent to scroll cancels it immediately: that is the whole difference
   * between correcting a landing and holding someone hostage to it.
   */
  const deadline = Date.now() + SETTLE_WINDOW_MS;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const stop = () => {
    if (timer !== undefined) clearTimeout(timer);
    for (const event of INTERRUPTS) window.removeEventListener(event, stop);
  };
  for (const event of INTERRUPTS) window.addEventListener(event, stop, { passive: true });

  const settle = () => {
    /*
     * "instant", not "auto". html carries scroll-behavior: smooth, and per
     * spec "auto" defers to that — so every pass started a fresh smooth
     * animation and cancelled the one before it. Firing one every 100ms meant
     * the page never arrived anywhere and scrolling appeared frozen for the
     * whole correction window. "instant" ignores the CSS and jumps.
     */
    el.scrollIntoView({ behavior: "instant", block: "center" });
    if (Date.now() > deadline) return stop();
    timer = setTimeout(settle, SETTLE_EVERY_MS);
  };
  settle();
  return "scrolled";
}

/*
 * Deep linking (#entry-id) is deliberately absent.
 *
 * Three approaches were tried and measured, all wrong in different ways:
 * scroll-then-correct-on-a-timer and scroll-then-correct-until-stable both
 * landed at exactly the same offset every time, and waiting for the stop count
 * to stabilise scrolls too early because all 81 render at once while their
 * heights are still contain-intrinsic-size estimates. The count settles
 * instantly; the geometry does not.
 *
 * The likely answer is to wait on actual layout — a ResizeObserver on the road
 * reporting no height change, or scrolling only once the target's own box has
 * been rendered rather than estimated. Left undone rather than shipped
 * landing in the wrong place, which is worse than no link at all.
 */
